import { PASSWORD_FORMAT_ERROR } from 'app/pages/account/userAccount/editMyProfile/components';
import { isEnvSandbox } from 'configs/api/enviroments';
import { RESELLER_IDS } from 'lib/const';
import { PackageName } from 'lib/const/PackageAdditionalInfo';
import {
  AUTOMOTIVE_RESELLER_ID,
  DEFAULT_10DLC_COUNTRY,
  NAPLETON_AUTOMOTIVE_RESELLER_ID,
  PASSWORD_ERROR_MESSAGE,
  SCREEN_VALUES,
} from 'lib/const/SuperAdminConstants';
import { ACTIVE_UNTIL } from 'lib/const/SuperAdminOptions';
import { VerificationStatus } from 'lib/const/VerificationStatus';
import { SuperAdminCustomerDetails, TableField } from 'lib/context';
import { generatePrettyPassword } from 'lib/utils/functions';
import {
  E164_REGEX,
  PASSWORD_REGEX,
  QUOTIBLE_DOMAIN_REGEX,
} from 'lib/utils/regexes';
import * as Yup from 'yup';
import { UserTableColumns } from '../../components/UserTable';
import { TABS } from './CustomersDetailsHeader';
import { TFunction } from 'i18next';

export const getTableFields = (t: TFunction): TableField[] => [
  {
    value: UserTableColumns.ACCESS,
    label: '',
    width: 50,
  },
  {
    value: UserTableColumns.AI_VIDEO_AGENTS_ENABLED,
    label: '',
    width: 30,
  },
  {
    value: UserTableColumns.FIRST_NAME,
    label: t('First Name'),
    sortable: true,
    sortKey: 'FIRST_NAME',
    width: 136,
  },
  {
    value: UserTableColumns.LAST_NAME,
    label: t('Last Name'),
    sortable: true,
    sortKey: 'LAST_NAME',
    width: 150,
  },
  {
    value: UserTableColumns.LOGINAS,
    label: '',
    width: 40,
  },
  {
    value: UserTableColumns.STATUS,
    label: t('Status'),
    width: 120,
  },
  {
    value: UserTableColumns.EMAIL,
    label: t('Email'),
    width: 284,
  },
  {
    value: UserTableColumns.CONTACT,
    label: t('Contact'),
    width: 110,
  },
  {
    value: UserTableColumns.LAST_LOGIN,
    label: t('Last Login'),
    width: 118,
  },
];

export const getTabs = (
  showSuperAdminOptions: boolean,
  showOptOutReports: boolean,
  t: TFunction
) => {
  return [
    {
      value: TABS.CUSTOMER_DETAILS,
      label: t('Customer Details'),
      showTab: true,
    },
    { value: TABS.USERS, label: t('Users'), showTab: showSuperAdminOptions },
    { value: TABS.USAGE, label: t('Usage'), showTab: showSuperAdminOptions },
    {
      value: TABS.ACTIVITY,
      label: t('Activity'),
      showTab: showSuperAdminOptions,
    },
    {
      value: TABS.OPT_OUT_REPORTS,
      label: t('Opt-out Reports'),
      showTab: showOptOutReports,
    },
    {
      value: TABS.MANAGE_INVITES,
      label: t('Manage Invites'),
      showTab: showSuperAdminOptions,
    },
  ];
};

const convertStringToDate = (value?: Date) => {
  if (!value) {
    return value;
  }
  const date = new Date(value);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const loadEditFields = (
  customerDataValues?: SuperAdminCustomerDetails
) => {
  return {
    customerId: customerDataValues?.customerId,
    resellerId: customerDataValues?.resellerId,
    codirectId: customerDataValues?.codirectId,
    business: customerDataValues?.business,
    firstName: customerDataValues?.firstName,
    lastName: customerDataValues?.lastName,
    email: customerDataValues?.email,
    emailsSentLimit: customerDataValues?.emailsSentLimit,
    verified: customerDataValues?.verified
      ? customerDataValues?.activeEndDate
        ? ACTIVE_UNTIL
        : VerificationStatus.ACTIVE
      : VerificationStatus.INACTIVE,
    newMaxUsers: customerDataValues?.newMaxUsers,
    maxUsers: customerDataValues?.maxUsers,
    phone1: customerDataValues?.phone1,
    phone2: customerDataValues?.phone2,
    url: customerDataValues?.url,
    referredBy: customerDataValues?.referredBy,
    referralAgent: customerDataValues?.referralAgent,
    accountManager: customerDataValues?.accountManager,
    crmProviderId: customerDataValues?.crmProviderId,
    customerLaunchDate: customerDataValues?.customerLaunchDate,
    activeEndDate: convertStringToDate(customerDataValues?.activeEndDate),
    wheelsTV: customerDataValues?.wheelsTV,
    liveAccess: customerDataValues?.liveAccess,
    hasCovideoMagic: customerDataValues?.hasCovideoMagic,
    maxPrompts: customerDataValues?.maxPrompts ?? 0,
    freeTrial: customerDataValues?.freeTrial ?? 0,
    smsEnabled: customerDataValues?.smsEnabled ?? 0,
    freeSmsPerMonth: customerDataValues?.freeSmsPerMonth,
    paidSmsPerMonth: customerDataValues?.paidSmsPerMonth,
    versionTwoEnabled: customerDataValues?.versionTwoEnabled,
    versionThreeEnabled: customerDataValues?.versionThreeEnabled,
    versionCDSEnabled: customerDataValues?.versionCDSEnabled,
    transcriptions: customerDataValues?.transcriptions ?? 0,
    transcriptionLicenses: customerDataValues?.transcriptionLicenses,
    location: customerDataValues?.location,
    number: customerDataValues?.number,
    country: customerDataValues?.country || DEFAULT_10DLC_COUNTRY,
    covideoPackageId: Number(customerDataValues?.packageId),
    remainingSmsCount: customerDataValues?.remainingSmsCount,
    sentSmsCount: customerDataValues?.sentSmsCount,
    customerOrganizations: customerDataValues?.customerOrganizations,
    salesforceAccountId: customerDataValues?.salesforceAccountId,
    credit700Url: customerDataValues?.credit700Url,
    vdpHomeNetEnabled: customerDataValues?.vdpHomeNetEnabled,
    vdpIPacketEnabled: customerDataValues?.vdpIPacketEnabled,
    lesaAccess: customerDataValues?.lesaAccess ?? 0,
    aiMusicEnabled: customerDataValues?.aiMusicEnabled ?? 0,
    spotlightEnabled: customerDataValues?.spotlightEnabled ?? 0,
    maxSpotlightPerMonth: customerDataValues?.maxSpotlightPerMonth ?? 0,
    quickVideoAvatarEnabled: customerDataValues?.quickVideoAvatarEnabled ?? 0,
    maxQuickVideoAvatarPerMonth:
      customerDataValues?.maxQuickVideoAvatarPerMonth ?? 0,
    isUsingVinReels: customerDataValues?.isUsingVinReels ?? 0,
    maxVinReelsPerMonth: customerDataValues?.maxVinReelsPerMonth ?? 0,
    quickVideosEnabled: customerDataValues?.quickVideosEnabled ?? 0,
    autominerEnabled: customerDataValues?.autominerEnabled,
    suggestedContentEnabled: customerDataValues?.suggestedContentEnabled ?? 0,
    preloadTeleprompterScripts:
      !!customerDataValues?.preloadTeleprompterScripts,
    weeklyReportEnabled: customerDataValues?.weeklyReportEnabled,
    allowContactCreation: customerDataValues?.allowContactCreation,
    autominerGroupId: customerDataValues?.autominerGroupId,
    oemDealerCode: customerDataValues?.oemDealerCode,
    oemAreaCode: customerDataValues?.oemAreaCode,
    oemSubAreaCode: customerDataValues?.oemSubAreaCode,
    timezone: customerDataValues?.timezone,
    automotiveAccess: customerDataValues?.automotiveAccess,
    jdPowerVDPEnabled: customerDataValues?.jdPowerVDPEnabled,
    hasLandingPageVDP: customerDataValues?.hasLandingPageVDP,
    manualPassword: customerDataValues?.manualPassword ?? false,
    quotibleAccess: customerDataValues?.quotibleAccess,
    quotibleLandingUrl: customerDataValues?.quotibleLandingUrl ?? '',
    quotibleIntegrationType: customerDataValues?.quotibleIntegrationType ?? '',
    customerLogo: { file: null, url: customerDataValues?.customerLogo || '' },
    mapIframeEmbedCode: customerDataValues?.mapIframeEmbedCode || '',
    isSetupComplete: customerDataValues?.isSetupComplete || false,
    smsProfileId: customerDataValues?.smsProfileId,
    customer10Dlc: {
      legalCompanyName:
        customerDataValues?.customer10Dlc?.legalCompanyName || '',
      legalStreet: customerDataValues?.customer10Dlc?.legalStreet || '',
      legalCity: customerDataValues?.customer10Dlc?.legalCity || '',
      legalState: customerDataValues?.customer10Dlc?.legalState || '',
      legalPostalCode: customerDataValues?.customer10Dlc?.legalPostalCode || '',
      ein: customerDataValues?.customer10Dlc?.ein || '',
      w9ContactEmail: customerDataValues?.customer10Dlc?.w9ContactEmail || '',
      w9ContactPhone: customerDataValues?.customer10Dlc?.w9ContactPhone || '',
      sampleVehicle: customerDataValues?.customer10Dlc?.sampleVehicle || '',
      websiteUrl: customerDataValues?.customer10Dlc?.websiteUrl || '',
      brandStatus: customerDataValues?.customer10Dlc?.brandStatus || '',
      campaignStatus: customerDataValues?.customer10Dlc?.campaignStatus || '',
      id: customerDataValues?.customer10Dlc?.id,
      telnyxBrandId: customerDataValues?.customer10Dlc?.telnyxBrandId,
      telnyxCampaignId: customerDataValues?.customer10Dlc?.telnyxCampaignId,
    },
    isUsingLandingPageDesign:
      customerDataValues?.isUsingLandingPageDesign ?? true,
  };
};

export const INIT_CUSTOMER_FIELDS = {
  resellerId: AUTOMOTIVE_RESELLER_ID,
  verified: VerificationStatus.ACTIVE,
  maxUsers: 1,
  wheelsTV: 0,
  freeTrial: 0,
  smsEnabled: 0,
  versionTwoEnabled: 0,
  versionThreeEnabled: 0,
  versionCDSEnabled: 1,
  covideoPackageId: PackageName.LEGACY,
  transcriptions: 0,
  liveAccess: 0,
  hasCovideoMagic: 0,
  maxPrompts: 0,
  vdpHomeNetEnabled: 0,
  vdpIPacketEnabled: 0,
  lesaAccess: 0,
  aiMusicEnabled: 0,
  spotlightEnabled: 0,
  maxSpotlightPerMonth: 0,
  isUsingVinReels: 0,
  maxVinReelsPerMonth: 0,
  quickVideoAvatarEnabled: 0,
  maxQuickVideoAvatarPerMonth: 0,
  suggestedContentEnabled: 0,
  quickVideosEnabled: 0,
  jdPowerVDPEnabled: 0,
  hasLandingPageVDP: 0,
  password: generatePrettyPassword(),
  manualPassword: false,
  customerLogo: { file: null, url: '' },
  mapIframeEmbedCode: '',
  isSetupComplete: false,
  customer10Dlc: {
    legalCompanyName: '',
    legalStreet: '',
    legalCity: '',
    legalState: '',
    legalPostalCode: '',
    ein: '',
    w9ContactEmail: '',
    w9ContactPhone: '',
    sampleVehicle: '',
    websiteUrl: '',
    id: undefined,
    telnyxBrandId: undefined,
    telnyxCampaignId: undefined,
  },
  isUsingLandingPageDesign: true,
};

export const validateSMSSetting = (smsEnabled: number, resellerId: number) =>
  !isEnvSandbox && !!smsEnabled && resellerId?.toString() !== RESELLER_IDS.CDK;
export const getFieldsSchema = (t: TFunction) =>
  Yup.object({
    firstName: Yup.string().required(t('First name is required')),
    lastName: Yup.string().when(['resellerId'], (resellerId: number) => {
      if (resellerId === NAPLETON_AUTOMOTIVE_RESELLER_ID) {
        return Yup.string()
          .nullable()
          .transform((v, o) => (o === '' ? undefined : v))
          .min(2, t('Last name is too small'));
      }
      return Yup.string()
        .required(t('Last name is required'))
        .min(2, t('Last name is too small'));
    }),

    quotibleLandingUrl: Yup.string().when('quotibleAccess', {
      is: true,
      then: Yup.string()
        .matches(
          QUOTIBLE_DOMAIN_REGEX,
          t(
            'Please enter a valid Quotible domain (e.g. mypage.quotible.com). Do not include "http://" or "www.".'
          )
        )
        .required(t('A Quotible Landing URL is required')),
      otherwise: Yup.string().notRequired(),
    }),
    quotibleIntegrationType: Yup.string().when('quotibleAccess', {
      is: true,
      then: Yup.string().required(t('A Quotible Integration Type is required')),
      otherwise: Yup.string().notRequired(),
    }),
    email: Yup.string()
      .email(t('Email is not valid'))
      .required(t('Email is required')),
    covideoPackageId: Yup.string().required(t('Package is required')),
    phone1: Yup.string()
      .min(
        7,
        t('Please enter a phone number equal or longer than 7 characters.')
      )
      .required(t('Your phone no. cannot be empty')),
    maxUsers: Yup.number().when(['verified'], (verified: boolean) => {
      if (verified) {
        return Yup.number().positive();
      }
      return Yup.number()
        .transform(value => (Number.isNaN(value) ? null : value))
        .nullable();
    }),
    freeTrial: Yup.number(),
    transcriptions: Yup.number(),
    transcriptionLicenses: Yup.number().when(
      ['transcriptions', 'maxUsers', 'verified'],
      (transcriptions: number, maxUsers: number, verified: boolean) => {
        if (transcriptions) {
          if (verified) {
            return Yup.number()
              .positive()
              .max(
                maxUsers,
                t('Caption Licenses cannot be more than user seats')
              )
              .required(t('Caption Licenses are required.'))
              .typeError(t('Caption Licenses are required'));
          }
          return Yup.number()
            .transform(value => (Number.isNaN(value) ? null : value))
            .nullable();
        }
      }
    ),
    smsEnabled: Yup.number(),
    location: Yup.string().when(
      ['smsEnabled', 'resellerId'],
      (smsEnabled: number, resellerId: number) => {
        if (validateSMSSetting(smsEnabled, resellerId)) {
          return Yup.string().required(t('SMS settings: Location is required'));
        }
      }
    ),
    number: Yup.string().when(
      ['smsEnabled', 'resellerId'],
      (smsEnabled: number, resellerId: number) => {
        if (validateSMSSetting(smsEnabled, resellerId)) {
          return Yup.string().required(
            t('SMS settings: Phone number is required')
          );
        }
      }
    ),
    customer10Dlc: Yup.object().shape({
      legalCompanyName: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Legal Business Name is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      legalStreet: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Legal Street is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      legalCity: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Legal City is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      legalState: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Legal State is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      legalPostalCode: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Legal Postal Code is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t, 4);
        }
      ),
      ein: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: EIN is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t, 9);
        }
      ),
      w9ContactEmail: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: W9 Contact Email is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      w9ContactPhone: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Dealer Contact Phone is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t, undefined, true);
        }
      ),
      sampleVehicle: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Sample Vehicle is required'),
        function (value) {
          return validateCustomer10DlcField(this, value, t);
        }
      ),
      websiteUrl: Yup.string().test(
        'required-based-on-parent',
        t('SMS settings: Website URL is required'),
        function (value) {
          return validateCustomer10DlcField(
            this,
            value,
            t,
            undefined,
            false,
            true
          );
        }
      ),
      brandStatus: Yup.string().notRequired(),
      campaignStatus: Yup.string().notRequired(),
    }),
    freeSmsPerMonth: Yup.number().when(
      ['smsEnabled', 'resellerId'],
      (smsEnabled: number, resellerId: number) => {
        if (validateSMSSetting(smsEnabled, resellerId)) {
          return Yup.number()
            .required(t('SMS settings: Free SMS per month field is required.'))
            .moreThan(
              -1,
              t(
                'SMS settings: Free SMS per month must be greater than or equal to 0'
              )
            )
            .typeError(t('Free SMS per month field is required.'));
        }
      }
    ),
    paidSmsPerMonth: Yup.number().when(
      ['smsEnabled', 'resellerId'],
      (smsEnabled: number, resellerId: number) => {
        if (validateSMSSetting(smsEnabled, resellerId)) {
          return Yup.number()
            .required(t('SMS settings: Paid SMS per month field is required.'))
            .positive(
              t('SMS settings: Paid SMS per month field must be greater than 0')
            )
            .typeError(
              t('SMS settings: Paid SMS per month field is required.')
            );
        }
      }
    ),
    username: Yup.string()
      .trim()
      .when(['customerId'], (customerId: number) => {
        if (!customerId) {
          return Yup.string()
            .trim()
            .required(t('Please enter a valid username.'));
        }
      }),
    password: Yup.string().when(['customerId'], (customerId: number) => {
      if (!customerId) {
        return Yup.string()
          .min(8, PASSWORD_ERROR_MESSAGE)
          .matches(PASSWORD_REGEX, PASSWORD_FORMAT_ERROR)
          .required(t('Please enter a valid password.'));
      }
    }),
    salesforceAccountId: Yup.string().required(
      t('Salesforce account Id is required')
    ),

    // codirectId: Yup.number()
    //   .typeError('Codirect ID must be a number.')
    //   .notRequired()
    //   .nullable(true),
  });

export const fieldsSchema = getFieldsSchema(
  ((key: string) => key) as TFunction
);

function validateCustomer10DlcField(
  context: Yup.TestContext,
  value: string,
  t: TFunction,
  minLength?: number,
  isPhone?: boolean,
  isUrl?: boolean
) {
  // Adding this temporarily, will remove this after some testing in production
  const skipValidation = true;
  if (skipValidation) {
    return true;
  }
  const { smsEnabled, resellerId, country, customer10Dlc, smsProfileId } =
    context.options.context as SuperAdminCustomerDetails;
  if (
    validateSMSSetting(smsEnabled || 0, resellerId || AUTOMOTIVE_RESELLER_ID) &&
    country === DEFAULT_10DLC_COUNTRY &&
    !customer10Dlc?.id &&
    !smsProfileId?.length
  ) {
    if (minLength && value && value.length < minLength) {
      return context.createError({
        message: t('This field must be at least {{minLength}} characters', {
          minLength,
        }),
      });
    }

    if (!!isPhone && (!value || !E164_REGEX.test(value))) {
      return context.createError({
        message: t('Dealer Contact Phone number must be in +E.164 format'),
      });
    }

    if (!!isUrl && value) {
      try {
        const urlObj = new URL(value);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
          throw new Error('Invalid protocol');
        }
      } catch {
        return context.createError({
          message: t(
            'Please enter a valid URL (must start with http:// or https://)'
          ),
        });
      }
    }

    return !!value;
  }
  return true;
}

export const getCustomerBreadcrumbs = (params: {
  fromScreen: string;
  resellerName: string;
  resellerId: number;
  isNewCustomer: boolean;
  customerId?: number;
  business?: string;
  t: TFunction;
}) => {
  const {
    fromScreen,
    resellerName,
    resellerId,
    isNewCustomer,
    customerId,
    business,
    t,
  } = params;
  const breadcrumbs = [
    {
      title:
        fromScreen === SCREEN_VALUES.RESELLERS
          ? t('Resellers')
          : t('Customers'),
      url:
        fromScreen === SCREEN_VALUES.RESELLERS
          ? '/admin/resellers'
          : '/admin/customers',
    },
  ];

  if (fromScreen === SCREEN_VALUES.RESELLERS) {
    breadcrumbs.push({
      title: resellerName,
      url: `/admin/resellers/${resellerId}`,
    });
  }

  breadcrumbs.push({
    title: isNewCustomer ? t('New Customer') : business || '',
    url: isNewCustomer
      ? '/admin/customers/create/1/0'
      : `/admin/customers/${customerId || 0}/1/0`,
  });

  return breadcrumbs;
};
